/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import {
  EcsperApiError,
  type EcsperClient,
  isEcsperApiError,
  isEcsperNetworkError,
  type TokenProvider,
} from '@ecsper/api-client';
import type {
  AmbiguousOfficeInfo,
  OfficeHost,
  OfficePlatform,
  OfficeSSOExchangeRequest,
  OfficeSSOExchangeResponse,
} from '@ecsper/types';

import type { AuthError, AuthState, AuthStatus } from './auth-state.js';
import {
  BOOTSTRAP_RETRY_DELAY_MS,
  MAX_BOOTSTRAP_RETRIES,
  MAX_EXPIRED_SSO_RECOVERY_ATTEMPTS,
  PREMATURE_TOKEN_RETRY_DELAY_MS,
  SSO_EXCHANGE_PATH,
} from './constants.js';
import { OfficePicker, parseAmbiguousOfficesFromMeta } from './office-picker.js';
import { acquireOfficeSSOToken, OfficeSSOBootstrapError } from './office-sso.js';
import { TokenManager } from './token-manager.js';

export interface AuthServiceConfig {
  /** Office host: `Word`, `Excel`, `Outlook` */
  readonly host: OfficeHost;
  /** Office host version (from `Office.context.diagnostics`) */
  readonly hostVersion: string;
  /** Office platform (from `Office.context.platform`) */
  readonly platform: OfficePlatform;
}

interface AuthServiceDeps {
  readonly sleep?: (ms: number) => Promise<void>;
  /** Extra delay after a 500 `RESIDENCY_RESOLUTION_FAILED` / `TOKEN_ISSUE_FAILED` before one repeat attempt. */
  readonly exchange500RetryDelayMs?: number;
}

class AmbiguousOfficeExchangeSignal extends Error {
  public readonly offices: readonly AmbiguousOfficeInfo[];

  public constructor(offices: readonly AmbiguousOfficeInfo[]) {
    super('AMBIGUOUS_OFFICE');
    this.name = 'AmbiguousOfficeExchangeSignal';
    this.offices = offices;
  }
}

/**
 * EcsperAuthService: the sole authentication entry point for the monorepo.
 *
 * Orchestrates:
 *   1. Office SSO bootstrap (`OfficeRuntime.auth`)
 *   2. Token exchange (`POST /platform/auth/office-sso` via {@link EcsperClient})
 *   3. Token lifecycle (acquire, cache, refresh)
 *   4. `AMBIGUOUS_OFFICE` resolution (picker flow)
 *   5. Error handling per CTR-PL-MS-OFFICESSO-001 §6.1
 *
 * The service is non-React. The React context provider wraps this service and exposes it via hook.
 *
 * Source: CTR-PL-MS-OFFICESSO-001 v1.0.0 (entire document)
 *         ECS-DD-MS365-001 v1.0.0 §B5, §B6
 */
export class EcsperAuthService {
  private readonly client: EcsperClient;
  private readonly config: AuthServiceConfig;
  private readonly tokenManager = new TokenManager();
  private readonly officePicker = new OfficePicker();
  private readonly listeners = new Set<(state: AuthState) => void>();
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly exchange500RetryDelayMs: number;

  private state: AuthState = {
    status: 'initialising',
    contextToken: null,
    actor: null,
    tenancy: null,
    engagement: null,
    apiBaseUrl: null,
    officeOptions: null,
    error: null,
    expiresAt: null,
  };

  public constructor(client: EcsperClient, config: AuthServiceConfig, deps?: AuthServiceDeps) {
    this.client = client;
    this.config = config;
    this.sleep = deps?.sleep ?? defaultSleep;
    this.exchange500RetryDelayMs = deps?.exchange500RetryDelayMs ?? 1000;

    this.tokenManager.onRefreshNeeded(() => {
      void this.refresh().catch(() => undefined);
    });
  }

  /** Start the full auth flow: bootstrap, exchange, authenticated */
  public async authenticate(): Promise<AuthState> {
    this.officePicker.clear();
    return await this.runAuthenticationPipeline({ officeIdHint: null, mode: 'initial' });
  }

  /** Re-authenticate with an `office_id_hint` (after `AMBIGUOUS_OFFICE`) */
  public async authenticateWithOffice(officeId: string): Promise<AuthState> {
    this.officePicker.selectOffice(officeId);
    return await this.runAuthenticationPipeline({ officeIdHint: officeId, mode: 'initial' });
  }

  public getState(): AuthState {
    return { ...this.state };
  }

  /** Subscribe to state changes */
  public subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Force refresh (re-bootstrap + re-exchange) */
  public async refresh(): Promise<AuthState> {
    return await this.runAuthenticationPipeline({ officeIdHint: null, mode: 'refresh' });
  }

  /** Logout (clear token, reset state) */
  public logout(): void {
    this.tokenManager.clear();
    this.officePicker.clear();
    this.state = {
      status: 'initialising',
      contextToken: null,
      actor: null,
      tenancy: null,
      engagement: null,
      apiBaseUrl: null,
      officeOptions: null,
      error: null,
      expiresAt: null,
    };
    this.notify();
  }

  /** Token provider callback for {@link EcsperClient} config (returns current Bearer token) */
  public getTokenProvider(): TokenProvider {
    return async () => {
      if (this.tokenManager.needsRefresh()) {
        void this.refresh().catch(() => undefined);
      }
      const managed = this.tokenManager.getToken();
      if (managed !== null && managed !== '') {
        return managed;
      }
      if (
        this.state.status === 'authenticated' &&
        this.state.contextToken !== null &&
        this.state.contextToken !== ''
      ) {
        return this.state.contextToken;
      }
      return null;
    };
  }

  private notify(): void {
    const snapshot = this.getState();
    for (const listener of this.listeners) listener(snapshot);
  }

  private patchPartial(patch: Partial<AuthState>): AuthState {
    this.state = { ...this.state, ...patch };
    this.notify();
    return this.getState();
  }

  private setStatus(status: AuthStatus): void {
    this.patchPartial({
      status,
      ...(status !== 'error' ? { error: null } : {}),
    });
  }

  private async runAuthenticationPipeline(args: {
    readonly officeIdHint: string | null;
    readonly mode: 'initial' | 'refresh';
  }): Promise<AuthState> {
    const startingStatus: AuthStatus = args.mode === 'refresh' ? 'refreshing' : 'bootstrapping';
    this.setStatus(startingStatus);
    if (args.mode === 'initial') {
      this.patchPartial({ officeOptions: null });
    }

    try {
      const officeToken = await this.bootstrapOfficeTokenWithRetries();
      this.setStatus('exchanging');

      const hint =
        args.officeIdHint !== null && args.officeIdHint.length > 0 ? args.officeIdHint : null;
      const body = this.buildExchangeRequest(officeToken, hint);

      try {
        const response = await this.exchangeWithRecoveries(body);
        return this.applyAuthenticated(response);
      } catch (error: unknown) {
        if (error instanceof AmbiguousOfficeExchangeSignal) {
          this.officePicker.setOptions([...error.offices]);
          this.tokenManager.clear();
          return this.patchPartial({
            status: 'office_picker',
            officeOptions: [...error.offices],
            contextToken: null,
            actor: null,
            tenancy: null,
            engagement: null,
            apiBaseUrl: null,
            expiresAt: null,
            error: null,
          });
        }
        throw error;
      }
    } catch (error: unknown) {
      return this.handleFatalAuthError(error);
    }
  }

  private async bootstrapOfficeTokenWithRetries(): Promise<string> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= MAX_BOOTSTRAP_RETRIES; attempt += 1) {
      try {
        return await acquireOfficeSSOToken();
      } catch (error: unknown) {
        lastError = error;
        if (attempt === MAX_BOOTSTRAP_RETRIES) break;
        await this.sleep(BOOTSTRAP_RETRY_DELAY_MS);
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Office SSO bootstrap failed');
  }

  private buildExchangeRequest(
    officeToken: string,
    officeIdHint: string | null,
  ): OfficeSSOExchangeRequest {
    const base: OfficeSSOExchangeRequest = {
      office_sso_token: officeToken,
      host: this.config.host,
      host_version: this.config.hostVersion,
      platform: this.config.platform,
    };
    if (officeIdHint !== null) {
      return { ...base, office_id_hint: officeIdHint };
    }
    return base;
  }

  private async exchangeWithRecoveries(
    initialBody: OfficeSSOExchangeRequest,
  ): Promise<OfficeSSOExchangeResponse> {
    let body: OfficeSSOExchangeRequest = initialBody;
    let prematureConsumed = false;
    let expiredRecoveries = 0;

    for (;;) {
      try {
        return await this.attemptExchangeWith500Retry(body);
      } catch (error: unknown) {
        if (!isEcsperApiError(error)) throw error;

        const code = error.errors[0]?.code;

        if (code === 'EXPIRED_SSO_TOKEN') {
          expiredRecoveries += 1;
          if (expiredRecoveries > MAX_EXPIRED_SSO_RECOVERY_ATTEMPTS) throw error;
          const nextToken = await this.bootstrapOfficeTokenWithRetries();
          body = { ...body, office_sso_token: nextToken };
          continue;
        }

        if (code === 'PREMATURE_SSO_TOKEN' && !prematureConsumed) {
          prematureConsumed = true;
          await this.sleep(PREMATURE_TOKEN_RETRY_DELAY_MS);
          continue;
        }

        if (code === 'AMBIGUOUS_OFFICE' && error.status === 409) {
          const offices = parseAmbiguousOfficesFromMeta(error.errors[0]?.meta);
          if (offices !== null) {
            throw new AmbiguousOfficeExchangeSignal(offices);
          }
        }

        throw error;
      }
    }
  }

  private async attemptExchangeWith500Retry(
    body: OfficeSSOExchangeRequest,
  ): Promise<OfficeSSOExchangeResponse> {
    try {
      return await this.postExchange(body);
    } catch (first: unknown) {
      if (!isEcsperApiError(first) || first.status !== 500) throw first;
      const code = first.errors[0]?.code;
      if (code !== 'RESIDENCY_RESOLUTION_FAILED' && code !== 'TOKEN_ISSUE_FAILED') throw first;
      await this.sleep(this.exchange500RetryDelayMs);
      return await this.postExchange(body);
    }
  }

  private async postExchange(body: OfficeSSOExchangeRequest): Promise<OfficeSSOExchangeResponse> {
    return await this.client.post<OfficeSSOExchangeResponse>(SSO_EXCHANGE_PATH, body);
  }

  private applyAuthenticated(response: OfficeSSOExchangeResponse): AuthState {
    this.officePicker.clear();
    this.tokenManager.setToken(response.context_token, response.expires_in);
    const expiresAt = Date.now() + response.expires_in * 1000;
    this.state = {
      status: 'authenticated',
      contextToken: response.context_token,
      actor: response.actor,
      tenancy: response.tenancy,
      engagement: response.engagement,
      apiBaseUrl: response.api_base_url,
      officeOptions: null,
      error: null,
      expiresAt,
    };
    this.notify();
    return this.getState();
  }

  private handleFatalAuthError(error: unknown): AuthState {
    if (error instanceof OfficeSSOBootstrapError) {
      const code =
        error.officeErrorCode !== undefined
          ? `OFFICE_SSO_${String(error.officeErrorCode)}`
          : 'OFFICE_SSO_UNKNOWN';
      return this.toErrorState({
        code,
        title: 'Office sign-in failed',
        detail: error.message,
        isRetryable: true,
      });
    }

    if (isEcsperApiError(error)) {
      return this.toErrorState(mapEcsperApiErrorToAuthError(error));
    }

    if (isEcsperNetworkError(error)) {
      return this.toErrorState({
        code: 'NETWORK_ERROR',
        title: 'Connection problem',
        detail: error.message,
        isRetryable: true,
      });
    }

    return this.toErrorState({
      code: 'UNKNOWN_ERROR',
      title: 'Authentication failed',
      detail: error instanceof Error ? error.message : 'Authentication failed.',
      isRetryable: false,
    });
  }

  private toErrorState(authError: AuthError): AuthState {
    this.tokenManager.clear();
    this.state = {
      status: 'error',
      contextToken: null,
      actor: null,
      tenancy: null,
      engagement: null,
      apiBaseUrl: null,
      officeOptions: null,
      expiresAt: null,
      error: authError,
    };
    this.notify();
    return this.getState();
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function mapEcsperApiErrorToAuthError(error: EcsperApiError): AuthError {
  const primary = error.errors[0];
  const code = primary?.code ?? `HTTP_${String(error.status)}`;
  const title = primary?.title ?? error.statusText;
  const detail = primary?.detail ?? error.message;

  switch (code) {
    case 'INVALID_SSO_TOKEN_SIGNATURE':
      return {
        code,
        title,
        detail: detail || 'Authentication failed; please re-open the add-in.',
        isRetryable: false,
      };
    case 'INVALID_SSO_TOKEN_ISSUER':
      return {
        code,
        title,
        detail: detail || 'Wrong Microsoft 365 account',
        isRetryable: false,
      };
    case 'INVALID_SSO_TOKEN_AUDIENCE':
      return {
        code,
        title,
        detail: detail || 'Configuration error; contact administrator',
        isRetryable: false,
      };
    case 'EXPIRED_SSO_TOKEN':
      return {
        code,
        title,
        detail: detail || 'The Office SSO token expired before exchange completed.',
        isRetryable: true,
      };
    case 'PREMATURE_SSO_TOKEN':
      return {
        code,
        title,
        detail: detail || 'Clock skew prevented token exchange; retry shortly.',
        isRetryable: true,
      };
    case 'MALFORMED_SSO_TOKEN':
      return {
        code,
        title,
        detail: detail || 'Authentication error',
        isRetryable: false,
      };
    case 'NO_ECSPER_USER':
      return {
        code,
        title,
        detail:
          detail ||
          'No Ecsper account found. Your administrator must provision your account before you can use Ecsper for Word.',
        isRetryable: false,
      };
    case 'NO_ORGANISATION_CONTEXT':
      return {
        code,
        title,
        detail: detail || 'No organisation assigned; contact administrator',
        isRetryable: false,
      };
    case 'NOT_ENTITLED':
      return {
        code,
        title,
        detail: detail || 'Ecsper for Word is not enabled for your organisation',
        isRetryable: false,
      };
    case 'RESIDENCY_RESOLUTION_FAILED':
      return {
        code,
        title,
        detail: detail || 'Service unavailable',
        isRetryable: true,
      };
    case 'TOKEN_ISSUE_FAILED':
      return {
        code,
        title,
        detail: detail || 'Service unavailable',
        isRetryable: true,
      };
    case 'RATE_LIMITED':
    case 'RATE_LIMIT_EXCEEDED':
      return {
        code,
        title,
        detail: detail || 'Too many sign-in attempts; wait and try again.',
        isRetryable: true,
      };
    case 'MALFORMED_REQUEST':
      return {
        code,
        title,
        detail: detail || 'The request could not be processed.',
        isRetryable: false,
      };
    case 'UNSUPPORTED_HOST':
      return {
        code,
        title,
        detail: detail || 'This Office host is not supported for Ecsper yet.',
        isRetryable: false,
      };
    case 'SERVICE_MISCONFIGURED':
      return {
        code,
        title,
        detail: detail || 'Service misconfiguration; contact administrator.',
        isRetryable: false,
      };
    default:
      return {
        code,
        title,
        detail,
        isRetryable: error.isRetryable,
      };
  }
}
